import { createFileRoute } from "@tanstack/react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DataTable,
  ExportCsvButton,
  FilterBar,
  SearchInput,
  Section,
  SelectFilter,
  StatCard,
} from "@/components/admin/admin-kit";
import { admAuditLog } from "@/lib/admin-pro.functions";
import { privatePageMetadata } from "@/lib/seo";

export const Route = createFileRoute("/nc/live/adm/logs")({
  head: () => privatePageMetadata("/nc/live/adm/logs", "Audit log | Control room", "Internal audit log."),
  component: AdminLogs,
});

const RANGES = [
  { value: "7", label: "7 days" },
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days" },
  { value: "365", label: "12 months" },
] as const;

function AdminLogs() {
  const run = useServerFn(admAuditLog);
  const [search, setSearch] = useState("");
  const [action, setAction] = useState("all");
  const [targetType, setTargetType] = useState("all");
  const [days, setDays] = useState<(typeof RANGES)[number]["value"]>("30");
  const [page, setPage] = useState(0);
  const pageSize = 50;

  const { data, isFetching } = useQuery({
    queryKey: ["adm-logs", search, action, targetType, days, page],
    placeholderData: keepPreviousData,
    queryFn: () =>
      run({
        data: {
          search: search || undefined,
          action: action === "all" ? undefined : action,
          targetType: targetType === "all" ? undefined : targetType,
          days: Number(days),
          page,
          pageSize,
        },
      }),
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard label="Events in range" value={total.toLocaleString()} />
        <StatCard label="Distinct actions" value={data?.actions.length ?? 0} />
        <StatCard label="Page" value={`${page + 1} / ${pages}`} />
      </div>

      <Section
        title="Audit log"
        description="Every administrative write is recorded here."
        actions={<ExportCsvButton rows={rows as unknown as Record<string, unknown>[]} filename="behot-audit-log" />}
      >
        <FilterBar>
          <SearchInput value={search} onChange={(v) => { setSearch(v); setPage(0); }} placeholder="Action or target id…" />
          <SelectFilter
            label="Action"
            value={action}
            onChange={(v) => { setAction(v); setPage(0); }}
            options={[{ value: "all", label: "All actions" }, ...(data?.actions ?? []).map((a) => ({ value: a, label: a }))]}
          />
          <SelectFilter
            label="Target"
            value={targetType}
            onChange={(v) => { setTargetType(v); setPage(0); }}
            options={[
              { value: "all", label: "All targets" },
              ...(data?.targetTypes ?? []).map((a) => ({ value: a, label: a })),
            ]}
          />
          <SelectFilter label="Range" value={days} onChange={(v) => { setDays(v); setPage(0); }} options={RANGES} />
        </FilterBar>

        <DataTable
          rows={rows}
          rowKey={(r) => r.id}
          empty={isFetching ? "Loading…" : "No audit events for this filter."}
          columns={[
            {
              key: "created_at",
              header: "When",
              cell: (r) => new Date(r.created_at).toLocaleString(),
              sortValue: (r) => r.created_at,
            },
            {
              key: "admin",
              header: "Admin",
              cell: (r) => r.admin?.display_name ?? r.admin_user_id.slice(0, 8),
            },
            { key: "action", header: "Action", cell: (r) => <code className="text-xs">{r.action}</code>, sortValue: (r) => r.action },
            {
              key: "target",
              header: "Target",
              cell: (r) => (
                <span className="text-xs text-muted-foreground">
                  {r.target_type ?? "—"} {r.target_id ? `· ${r.target_id.slice(0, 8)}` : ""}
                </span>
              ),
            },
            {
              key: "detail",
              header: "Detail",
              className: "max-w-[380px]",
              cell: (r) => (
                <pre className="max-h-24 overflow-auto whitespace-pre-wrap break-all rounded bg-muted/60 p-2 text-[11px]">
                  {JSON.stringify(r.detail ?? {}, null, 1)}
                </pre>
              ),
            },
          ]}
        />

        <div className="mt-4 flex items-center justify-between">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <span className="text-xs text-muted-foreground">
            {total.toLocaleString()} events · page {page + 1} of {pages}
          </span>
          <Button variant="outline" size="sm" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </div>
      </Section>
    </div>
  );
}
